import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useCallback,
  Dispatch,
  SetStateAction
} from 'react'

import { MentorCloudFile } from 'src/types/files/file'

interface MentorCloudUploadFile {
  file: File
  progress: number
  convertProgress?: number
  parentFolderId?: number | null
}

type MentorCloudContextProps = {
  files?: MentorCloudFile[]
  onChangeFiles?: (value: MentorCloudFile[], singleFile?: boolean) => void
  uploadFiles?: MentorCloudUploadFile[]
  onChangeUploadFiles?: Dispatch<SetStateAction<MentorCloudUploadFile[]>>
  onUploadFilesChangeProgress?: (file: Blob, progress: number) => void
  reset?: () => void
}

const defaultValue: MentorCloudContextProps = {}

const MentorCloudContext = createContext<MentorCloudContextProps | undefined>(defaultValue)

export const MentorCloudProvider = ({ children }: { children: ReactNode }) => {
  const [files, setFiles] = useState<MentorCloudFile[]>()
  const [uploadFiles, setUploadFiles] = useState<MentorCloudUploadFile[]>([])

  const onChangeFiles = useCallback((value: MentorCloudFile[], singleFile = true) => {
    if (singleFile) {
      setFiles([value[0]])

      return
    }

    setFiles(value)
  }, [])

  const onUploadFilesChangeProgress = (file: Blob, progress: number) => {
    setUploadFiles?.(prev =>
      prev.map(uploadedFile => {
        if (uploadedFile.file.name === file.name) {
          return {
            ...uploadedFile,
            progress
          }
        }

        return uploadedFile
      })
    )
  }

  const value = useMemo(
    () => ({
      files,
      onChangeFiles,
      reset: () => setFiles([]),
      uploadFiles,
      onChangeUploadFiles: setUploadFiles,
      onUploadFilesChangeProgress
    }),
    [files, onChangeFiles, uploadFiles]
  )

  return <MentorCloudContext.Provider value={value}>{children}</MentorCloudContext.Provider>
}

export const useMentorCloud = () => {
  const context = useContext(MentorCloudContext)

  if (!context) throw new Error('XContext must be called from within the XContextProvider')

  return context
}
